import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    ColumnDef,
    ColumnFiltersState,
    PaginationState,
    SortingState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, MoreHorizontal, Plus, Edit, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trpc } from '~/api/trpc';
import Content from '../components/layout/Content';
import { Skeleton } from '../components/ui/skeleton';
import { useShallow } from 'zustand/react/shallow';
import { useTreatmentsTableFilteringStore } from '~/stores/treatments-table-filtering-store';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';

// Define the GOA Treatment type based on the NewGoaTreatment model
export type GoaTreatment = {
    goaNo: string;
    createdAt: string;
    name: string;
    basePoints: number;
    billingType: 'STATIC' | 'DYNAMIC'; // Adjust based on your actual enum
};

// GOA Treatment Form Dialog Component
const GoaTreatmentDialog = ({ 
    treatment, 
    isOpen, 
    onOpenChange, 
    mode 
}: { 
    treatment?: GoaTreatment; 
    isOpen: boolean; 
    onOpenChange: (open: boolean) => void;
    mode: 'create' | 'edit';
}) => {
    const [formData, setFormData] = useState({
        goaNo: treatment?.goaNo || '',
        name: treatment?.name || '',
        basePoints: treatment?.basePoints || 0,
        billingType: treatment?.billingType || 'STATIC' as const,
    });

    const utils = trpc.useUtils();
    const createMutation = trpc.goaTreatments.create.useMutation();
    const updateMutation = trpc.goaTreatments.update.useMutation();

    // Reset form when treatment changes or dialog opens/closes
    useEffect(() => {
        if (isOpen) {
            setFormData({
                goaNo: treatment?.goaNo || '',
                name: treatment?.name || '',
                basePoints: treatment?.basePoints || 0,
                billingType: treatment?.billingType || 'STATIC',
            });
        }
    }, [treatment, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        try {
            if (mode === 'create') {
                await createMutation.mutateAsync(formData);
                toast.success('GOA Treatment created successfully');
            } else {
                await updateMutation.mutateAsync(formData);
                toast.success('GOA Treatment updated successfully');
            }
            
            // Invalidate and refetch queries
            await utils.goaTreatments.getAll.invalidate();
            onOpenChange(false);
        } catch (error) {
            toast.error(`Failed to ${mode} GOA Treatment`);
        }
    };

    const isLoading = createMutation.isPending || updateMutation.isPending;

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[525px]">
                <DialogHeader>
                    <DialogTitle>
                        {mode === 'create' ? 'Create New GOA Treatment' : 'Edit GOA Treatment'}
                    </DialogTitle>
                    <DialogDescription>
                        {mode === 'create' 
                            ? 'Fill in the details for the new GOA treatment.' 
                            : 'Make changes to the GOA treatment details.'
                        }
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="goaNo">GOA Number</Label>
                            <Input
                                id="goaNo"
                                value={formData.goaNo}
                                onChange={(e) => setFormData(prev => ({ ...prev, goaNo: e.target.value }))}
                                placeholder="Enter GOA number"
                                disabled={mode === 'edit'} // GOA number shouldn't be editable
                                required
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="name">Name</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Enter treatment name"
                                required
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="basePoints">Base Points</Label>
                            <Input
                                id="basePoints"
                                type="number"
                                min="0"
                                value={formData.basePoints}
                                onChange={(e) => setFormData(prev => ({ ...prev, basePoints: parseInt(e.target.value) || 0 }))}
                                placeholder="Enter base points"
                                required
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="billingType">Billing Type</Label>
                            <Select
                                value={formData.billingType}
                                onValueChange={(value: 'STATIC' | 'DYNAMIC') => 
                                    setFormData(prev => ({ ...prev, billingType: value }))
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select billing type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="STATIC">Static</SelectItem>
                                    <SelectItem value="DYNAMIC">Dynamic</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {mode === 'create' ? 'Create' : 'Update'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

// Delete Confirmation Dialog
const DeleteGoaTreatmentDialog = ({ 
    treatment, 
    isOpen, 
    onOpenChange 
}: { 
    treatment?: GoaTreatment; 
    isOpen: boolean; 
    onOpenChange: (open: boolean) => void;
}) => {
    const utils = trpc.useUtils();
    const deleteMutation = trpc.goaTreatments.delete.useMutation();

    const handleDelete = async () => {
        if (!treatment) return;
        
        try {
            await deleteMutation.mutateAsync({ goaNo: treatment.goaNo });
            toast.success('GOA Treatment deleted successfully');
            await utils.goaTreatments.getAll.invalidate();
            onOpenChange(false);
        } catch (error) {
            toast.error('Failed to delete GOA Treatment');
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Delete GOA Treatment</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete "{treatment?.name}"? This action cannot be undone.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={deleteMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Create Treatment Button
const CreateGoaTreatmentButton = () => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    return (
        <>
            <Button
                variant='outline'
                className='border-primary-400 text-primary-400 hover:text-primary-400 hover:bg-primary-400/10 transition-all'
                onClick={() => setIsDialogOpen(true)}
            >
                <Plus className='mr-1 h-4 w-4' />
                New GOA Treatment
            </Button>
            <GoaTreatmentDialog
                mode="create"
                isOpen={isDialogOpen}
                onOpenChange={setIsDialogOpen}
            />
        </>
    );
};

// Table columns definition
export const columns: ColumnDef<GoaTreatment>[] = [
    {
        accessorKey: 'goaNo',
        header: 'GOA Number',
    },
    {
        accessorKey: 'name',
        header: ({ column }) => {
            return (
                <Button
                    variant='ghost'
                    onClick={() => {
                        const currentSort = column.getIsSorted();
                        if (currentSort === 'asc') {
                            column.toggleSorting(true);
                        } else if (currentSort === 'desc') {
                            column.clearSorting();
                        } else {
                            column.toggleSorting(false);
                        }
                    }}
                >
                    Name
                    {column.getIsSorted() === 'asc' ? (
                        <ArrowUp className='ml-2 h-4 w-4' />
                    ) : column.getIsSorted() === 'desc' ? (
                        <ArrowDown className='ml-2 h-4 w-4' />
                    ) : (
                        <ArrowUpDown className='ml-2 h-4 w-4 opacity-50' />
                    )}
                </Button>
            );
        },
    },
    {
        accessorKey: 'basePoints',
        header: 'Base Points',
        cell: ({ row }) => {
            return <div>{row.original.basePoints}</div>;
        },
    },
    {
        accessorKey: 'billingType',
        header: 'Billing Type',
        cell: ({ row }) => {
            return <div className='capitalize'>{row.original.billingType.toLowerCase()}</div>;
        },
    },
    {
        id: 'createdAt',
        header: 'Created At',
        cell: ({ row }) => {
            const date = new Date(row.original.createdAt);
            return <div>{format(date, 'PPP')}</div>;
        },
        accessorFn: (row) => new Date(row.createdAt).getTime(),
    },
    {
        id: 'actions',
        cell: ({ row }) => {
            const treatment = row.original;
            const [editDialogOpen, setEditDialogOpen] = useState(false);
            const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

            return (
                <>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant='ghost' className='h-8 w-8 p-0'>
                                <span className='sr-only'>Open menu</span>
                                <MoreHorizontal className='h-4 w-4' />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => navigator.clipboard.writeText(treatment.goaNo)}>
                                Copy GOA number
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>
                                <Edit className='mr-2 h-4 w-4' />
                                Edit treatment
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                                onClick={() => setDeleteDialogOpen(true)}
                                className='text-red-600 focus:text-red-600'
                            >
                                <Trash2 className='mr-2 h-4 w-4' />
                                Delete treatment
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    
                    <GoaTreatmentDialog
                        treatment={treatment}
                        mode="edit"
                        isOpen={editDialogOpen}
                        onOpenChange={setEditDialogOpen}
                    />
                    
                    <DeleteGoaTreatmentDialog
                        treatment={treatment}
                        isOpen={deleteDialogOpen}
                        onOpenChange={setDeleteDialogOpen}
                    />
                </>
            );
        },
    },
];

// Data Table Props Interface
interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    manualPagination?: boolean;
    manualSorting?: boolean;
    manualFiltering?: boolean;
    syncWithUrl?: boolean;
}

// Main Data Table Component
export const DataTable = <TData, TValue>({
    columns,
    manualPagination = true,
    manualSorting = true,
    manualFiltering = true,
    syncWithUrl = true,
}: DataTableProps<TData, TValue>) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isInitialized = useRef<boolean>(false);

    // Get state and setters from Zustand store
    const {
        sorting: storeSorting,
        columnFilters: storeColumnFilters,
        pagination: storePagination,
        setSorting,
        setColumnFilters,
        setPagination,
    } = useTreatmentsTableFilteringStore(
        useShallow((state) => ({
            sorting: state.sorting,
            setSorting: state.setSorting,
            columnFilters: state.columnFilters,
            setColumnFilters: state.setColumnFilters,
            pagination: state.pagination,
            setPagination: state.setPagination,
        }))
    );

    // Initial State Synchronization Effect
    useEffect(() => {
        if (!syncWithUrl || isInitialized.current) {
            return;
        }
        isInitialized.current = true;

        const newParams = new URLSearchParams(searchParams);
        let needsUrlUpdate = false;

        // Sync Sorting
        const urlSortField = searchParams.get('sort');
        const urlSortDir = searchParams.get('dir') || 'asc';
        let currentSorting = storeSorting;

        if (urlSortField) {
            const urlSortingState: SortingState = [{ id: urlSortField, desc: urlSortDir === 'desc' }];
            if (JSON.stringify(urlSortingState) !== JSON.stringify(storeSorting)) {
                setSorting(urlSortingState);
                currentSorting = urlSortingState;
            }
            if (!searchParams.has('dir') && urlSortField) {
                newParams.set('dir', 'asc');
                needsUrlUpdate = true;
            }
        } else if (storeSorting.length > 0) {
            newParams.set('sort', storeSorting[0].id);
            newParams.set('dir', storeSorting[0].desc ? 'desc' : 'asc');
            needsUrlUpdate = true;
        } else {
            if (searchParams.has('sort')) { newParams.delete('sort'); needsUrlUpdate = true; }
            if (searchParams.has('dir')) { newParams.delete('dir'); needsUrlUpdate = true; }
        }

        // Sync Filtering
        const urlFilters: ColumnFiltersState = [];
        let hasUrlFilters = false;
        for (const [key, value] of searchParams.entries()) {
            if (key.startsWith('filter_')) {
                hasUrlFilters = true;
                urlFilters.push({
                    id: key.replace('filter_', ''),
                    value: value,
                });
            }
        }

        let currentFilters = storeColumnFilters;
        if (hasUrlFilters) {
            if (JSON.stringify(urlFilters.sort((a, b) => a.id.localeCompare(b.id))) !== JSON.stringify(storeColumnFilters.sort((a, b) => a.id.localeCompare(b.id)))) {
                setColumnFilters(urlFilters);
                currentFilters = urlFilters;
            }
        } else if (storeColumnFilters.length > 0) {
            storeColumnFilters.forEach(filter => {
                newParams.set(`filter_${filter.id}`, String(filter.value));
            });
            needsUrlUpdate = true;
        } else {
            Array.from(searchParams.keys())
                .filter(key => key.startsWith('filter_'))
                .forEach(key => {
                    newParams.delete(key);
                    needsUrlUpdate = true;
                });
        }

        // Sync Pagination
        const urlPage = searchParams.get('page');
        const urlSize = searchParams.get('size');
        let currentPagination = storePagination;
        const paginationUpdateFromUrl: Partial<PaginationState> = {};
        let urlHasPagination = false;

        if (urlPage !== null) {
            const pageIndex = Math.max(0, parseInt(urlPage, 10) || 0);
            paginationUpdateFromUrl.pageIndex = pageIndex;
            urlHasPagination = true;
        }

        if (urlSize !== null) {
            const pageSize = Math.max(1, parseInt(urlSize, 10) || storePagination.pageSize);
            paginationUpdateFromUrl.pageSize = pageSize;
            urlHasPagination = true;
        }

        if (urlHasPagination) {
            const newPaginationState = { ...storePagination, ...paginationUpdateFromUrl };
            if (newPaginationState.pageIndex !== storePagination.pageIndex || newPaginationState.pageSize !== storePagination.pageSize) {
                setPagination(newPaginationState);
                currentPagination = newPaginationState;
            }
            if (String(currentPagination.pageIndex) !== urlPage) {
                newParams.set('page', String(currentPagination.pageIndex));
                needsUrlUpdate = true;
            }
            if (String(currentPagination.pageSize) !== urlSize) {
                newParams.set('size', String(currentPagination.pageSize));
                needsUrlUpdate = true;
            }
        } else {
            newParams.set('page', String(storePagination.pageIndex));
            newParams.set('size', String(storePagination.pageSize));
            needsUrlUpdate = true;
        }

        if (needsUrlUpdate) {
            setSearchParams(newParams, { replace: true });
        }
    }, [syncWithUrl, searchParams, setSearchParams]);

    // Derive API Query Params from the URL
    const queryPage = parseInt(searchParams.get('page') || String(storePagination.pageIndex), 10);
    const queryPageSize = parseInt(searchParams.get('size') || String(storePagination.pageSize), 10);
    const querySortField = searchParams.get('sort') || undefined;
    const querySortDirection = (searchParams.get('dir') || undefined) as 'asc' | 'desc' | undefined;
    const queryFilters: Record<string, string | undefined> = {};
    for (const [key, value] of searchParams.entries()) {
        if (key.startsWith('filter_')) {
            queryFilters[key.replace('filter_', '')] = value;
        }
    }

    // Data Fetching
    const {
        data: goaTreatmentsResponse,
        isLoading: isQueryLoading,
        isFetching: isQueryFetching,
    } = trpc.goaTreatments.getAll.useQuery(
        {
            page: queryPage,
            pageSize: queryPageSize,
            sortField: querySortField,
            sortDirection: querySortDirection,
            filters: queryFilters,
        },
        {
            suspense: true,
        }
    );

    const isLoading = isQueryLoading;
    const isFetching = isQueryFetching;

    // State Synchronization Effects (State -> URL)
    useEffect(() => {
        if (!syncWithUrl || !isInitialized.current) return;
        const currentSortField = searchParams.get('sort');
        const currentSortDir = searchParams.get('dir');
        const storeSortField = storeSorting[0]?.id;
        const storeSortDir = storeSorting[0]?.desc ? 'desc' : 'asc';

        if (storeSortField !== currentSortField || (storeSortField && storeSortDir !== currentSortDir)) {
            const newParams = new URLSearchParams(searchParams);
            if (storeSorting.length > 0) {
                newParams.set('sort', storeSorting[0].id);
                newParams.set('dir', storeSorting[0].desc ? 'desc' : 'asc');
            } else {
                newParams.delete('sort');
                newParams.delete('dir');
            }
            setSearchParams(newParams, { replace: true });
        }
    }, [storeSorting, syncWithUrl, searchParams, setSearchParams]);

    useEffect(() => {
        if (!syncWithUrl || !isInitialized.current) return;
        const newParams = new URLSearchParams(searchParams);
        let filtersChanged = false;

        const urlFiltersMap = new Map<string, string>();
        Array.from(newParams.keys())
            .filter((key) => key.startsWith('filter_'))
            .forEach((key) => urlFiltersMap.set(key.replace('filter_', ''), newParams.get(key)!));

        const storeFiltersMap = new Map<string, string>();
        storeColumnFilters.forEach(f => storeFiltersMap.set(f.id, String(f.value)));

        urlFiltersMap.forEach((_, id) => {
            if (!storeFiltersMap.has(id)) {
                newParams.delete(`filter_${id}`);
                filtersChanged = true;
            }
        });

        storeFiltersMap.forEach((value, id) => {
            if (urlFiltersMap.get(id) !== value) {
                newParams.set(`filter_${id}`, value);
                filtersChanged = true;
            }
        });

        if (filtersChanged) {
            setSearchParams(newParams, { replace: true });
        }
    }, [storeColumnFilters, syncWithUrl, searchParams, setSearchParams]);

    useEffect(() => {
        if (!syncWithUrl || !isInitialized.current) return;
        const currentPage = searchParams.get('page');
        const currentSize = searchParams.get('size');
        const storePage = String(storePagination.pageIndex);
        const storeSize = String(storePagination.pageSize);

        if (currentPage !== storePage || currentSize !== storeSize) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('page', storePage);
            newParams.set('size', storeSize);
            setSearchParams(newParams, { replace: true });
        }
    }, [storePagination, syncWithUrl, searchParams, setSearchParams]);

    // React Table Instance
    const [rowSelection, setRowSelection] = useState({});
    const table = useReactTable({
        data: goaTreatmentsResponse?.data ?? [],
        columns,
        pageCount: goaTreatmentsResponse?.pageCount ?? -1,
        state: {
            sorting: storeSorting,
            columnFilters: storeColumnFilters,
            pagination: storePagination,
            rowSelection,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onRowSelectionChange: setRowSelection,
        manualPagination,
        manualSorting,
        manualFiltering,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
        getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
        getFilteredRowModel: manualFiltering ? undefined : getFilteredRowModel(),
    });

    // Clamp Page Index Effect
    useEffect(() => {
        if (!goaTreatmentsResponse || isLoading || isFetching || !manualPagination) {
            return;
        }
        const currentPageIndex = table.getState().pagination.pageIndex;
        const pageCount = goaTreatmentsResponse.pageCount;
        if (pageCount > 0 && currentPageIndex >= pageCount) {
            table.setPageIndex(Math.max(0, pageCount - 1));
        }
    }, [goaTreatmentsResponse, table, isLoading, isFetching, manualPagination]);

    // Loading Skeleton
    if (isLoading) {
        return (
            <div className='bg-white rounded-lg shadow-sm p-6'>
                <div className='flex items-center justify-between py-4'>
                    <Skeleton className='h-10 w-[250px]' />
                    <Skeleton className='h-10 w-[150px]' />
                </div>
                <div className='rounded-md border border-gray-200'>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {Array.from({ length: columns.length }).map((_, index) => (
                                    <TableHead key={index}>
                                        <Skeleton className='h-6 w-full' />
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: table.getState().pagination.pageSize }).map((_, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {Array.from({ length: columns.length }).map((_, colIndex) => (
                                        <TableCell key={colIndex}>
                                            <Skeleton className='h-6 w-full' />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
                    <Skeleton className='h-6 w-24' />
                    <div className="flex space-x-2">
                        <Skeleton className='h-9 w-20' />
                        <Skeleton className='h-9 w-20' />
                    </div>
                </div>
            </div>
        );
    }

    // Main Table Render
    return (
        <div className='bg-white rounded-lg shadow-sm p-6'>
            {/* Filter Input & Add Button */}
            <div className='flex items-center justify-between py-4'>
                <div className='flex gap-4 items-center'>
                    <Input
                        placeholder='Filter by name...'
                        value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
                        onChange={(event) => {
                            table.getColumn('name')?.setFilterValue(event.target.value)
                        }}
                        className='max-w-sm focus:ring-primary'
                    />
                    <Input
                        placeholder='Filter by GOA number...'
                        value={(table.getColumn('goaNo')?.getFilterValue() as string) ?? ''}
                        onChange={(event) => {
                            table.getColumn('goaNo')?.setFilterValue(event.target.value)
                        }}
                        className='max-w-sm focus:ring-primary'
                    />
                    {isFetching && (
                        <div className='flex items-center text-sm text-muted-foreground'>
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            Loading...
                        </div>
                    )}
                </div>
                <CreateGoaTreatmentButton />
            </div>

            {/* Table */}
            <div className='rounded-md border border-gray-200 overflow-hidden'>
                <Table>
                    <TableHeader className='bg-gray-50'>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead key={header.id} className='font-medium text-gray-700'>
                                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && 'selected'}
                                    className='hover:bg-gray-50 transition-colors'
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id} style={{ width: cell.column.getSize() !== 150 ? cell.column.getSize() : undefined }}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className='h-24 text-center'>
                                    {isFetching ? (
                                        <div className='flex justify-center items-center'>
                                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            Loading data...
                                        </div>
                                    ) : (
                                        queryFilters && Object.keys(queryFilters).length > 0
                                            ? 'No GOA treatments found matching your filters.'
                                            : 'No GOA treatments found.'
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination Controls */}
            <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
                <div className='flex items-center space-x-2'>
                    <select
                        value={table.getState().pagination.pageSize}
                        onChange={e => {
                            table.setPageSize(Number(e.target.value))
                        }}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                        disabled={isFetching}
                    >
                        {[10, 20, 30, 40, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </select>
                    <div className='text-sm text-muted-foreground'>
                        Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount() > 0 ? table.getPageCount() : 1}
                    </div>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage() || isFetching}
                        className='border-gray-300 hover:bg-gray-50'
                    >
                        Previous
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage() || isFetching}
                        className='border-gray-300 hover:bg-gray-50'
                    >
                        Next
                    </Button>
                </div>
            </div>
        </div>
    );
};

// Main GOA Treatments component
const GoaTreatments = () => {
    return (
        <Content>
            <div className='container mx-auto py-10'>
                <h1 className='text-2xl font-bold mb-6'>GOA Treatments</h1>
                <DataTable 
                    columns={columns} 
                    manualPagination={true} 
                    manualSorting={true} 
                    manualFiltering={true} 
                    syncWithUrl={true} 
                />
            </div>
        </Content>
    );
};

export default GoaTreatments;